import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Github, Compass } from "lucide-react";
import api from "../lib/api";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";

const HEALTH_TONE = { AT_RISK: "red", WATCH: "amber", BUILDING: "blue", HEALTHY: "green", UNKNOWN: "dim" };
const STATUS_TONE = { ACTIVE: "green", INCUBATING: "purple", ARCHIVED: "dim" };

function BusinessName({ name }) {
  const idx = name.toLowerCase().lastIndexOf("linc");
  if (idx === -1) return <>{name}</>;
  return <>{name.slice(0, idx)}<span className="linc">{name.slice(idx, idx + 4)}</span>{name.slice(idx + 4)}</>;
}

export default function BusinessDetailPage() {
  const { key } = useParams();
  const [biz, setBiz] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/businesses/${key}`);
        setBiz(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Not found");
      }
    })();
  }, [key]);

  if (err) {
    return (
      <>
        <TopBar title="Business" />
        <div className="panel"><div className="panel-body"><EmptyState title={String(err)} /></div></div>
      </>
    );
  }
  if (!biz) return <TopBar title="Business" subtitle="Loading…" />;

  const isCommandLinc = (biz.key || biz.slug) === "commandlinc";

  return (
    <div data-edit-name="Business detail">
      <TopBar
        title={<BusinessName name={biz.name} />}
        subtitle={biz.description}
        actions={
          <Link to="/businesses" className="btn small"><ArrowLeft size={12} /> All businesses</Link>
        }
      />

      <div className="panel" style={{ marginBottom: 14 }} data-testid="biz-detail-tile" data-edit-name="Business summary">
        <div className="panel-head">
          <div>
            <h2>
              <span className={`pill ${STATUS_TONE[biz.status] || "dim"}`}>{biz.status}</span>{" "}
              <span className="pill dim">{biz.business_type?.replace(/_/g, " ")}</span>{" "}
              <span className={`pill ${HEALTH_TONE[biz.health] || "dim"}`}>{biz.health}</span>
            </h2>
          </div>
          {biz.github?.repo_url ? (
            <a href={biz.github.repo_url} target="_blank" rel="noreferrer" className="btn small">
              <Github size={12} /> Repo <ExternalLink size={10} />
            </a>
          ) : null}
        </div>
        <div className="panel-body">
          {biz.current_milestone ? (
            <p><small className="hint">CURRENT MILESTONE</small><br /><b>{biz.current_milestone}</b></p>
          ) : null}
          {biz.target_date ? (
            <p style={{ marginTop: 6 }}><small className="hint">TARGET</small>{" "}<span className="mono">{biz.target_date}</span></p>
          ) : null}
          {isCommandLinc ? (
            <div style={{ marginTop: 14 }}>
              <Link to="/commandlinc" className="btn small primary">
                <Compass size={12} /> Open CommandLinc module
              </Link>
            </div>
          ) : null}
        </div>
      </div>

      <div className="panel" data-edit-name="Business notes">
        <div className="panel-head">
          <div><h2>More detail</h2><p>Business-level workspaces land in a future phase.</p></div>
        </div>
        <div className="panel-body">
          <EmptyState title="Not yet connected — GameLinc / GearLinc / ForgeLinc workspaces will unlock in a later phase." />
        </div>
      </div>
    </div>
  );
}
